import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Flame, Clock, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import WorkoutPrograms from "@/components/WorkoutPrograms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const shortDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long" }); } catch { return iso; } };

export default function MemberWorkout() {
  const [me, setMe] = useState(null);
  const [logs, setLogs] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", duration_min: "" });
  const [saving, setSaving] = useState(false);

  const loadLogs = (id) => api.get(`/members/${id}/workout-logs`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  useEffect(() => { api.get("/members/me").then((r) => { setMe(r.data); loadLogs(r.data.id); }).catch(() => setMe(false)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/members/${me.id}/workout-logs`, { title: form.title || "Antrenman", duration_min: form.duration_min ? Number(form.duration_min) : null });
      toast.success("Antrenman kaydedildi 💪");
      setOpen(false); setForm({ title: "", duration_min: "" }); loadLogs(me.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (me === null) return <Skeleton className="h-64 rounded-2xl bg-[#13131C]" />;
  if (!me) return <div className="text-center py-20 text-[#68687B]">Üye profili bulunamadı.</div>;

  return (
    <div data-testid="member-workout-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-white">Antrenmanım</h1>
        <Button onClick={() => setOpen(true)} data-testid="log-workout-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl h-10"><Plus size={17} className="mr-1" /> Kaydet</Button>
      </div>
      <p className="text-sm text-[#68687B] mb-2">Programın ve antrenman geçmişin</p>

      <WorkoutPrograms memberId={me.id} canEdit={false} />

      <div className="mt-8">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#68687B] mb-3 flex items-center gap-2"><Flame size={14} className="text-[#FF5500]" /> Antrenman Geçmişi</h3>
        {logs === null ? <Skeleton className="h-16 rounded-2xl bg-[#13131C]" /> : logs.length === 0 ? (
          <div className="text-center py-10 text-[#68687B] rounded-2xl border border-[#222230] bg-[#13131C]">Henüz kayıt yok. İlk antrenmanını kaydet!</div>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id} data-testid={`log-${l.id}`} className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#13131C] border border-[#222230]">
                <div className="w-9 h-9 rounded-xl bg-[#FF5500]/10 flex items-center justify-center"><Flame size={16} className="text-[#FF5500]" /></div>
                <div className="flex-1"><p className="text-sm font-semibold text-white">{l.title || "Antrenman"}</p>
                  <p className="text-xs text-[#68687B] flex items-center gap-2"><CalendarDays size={11} /> {shortDate(l.date)}{l.duration_min ? <><Clock size={11} className="ml-1" /> {l.duration_min} dk</> : null}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Antrenman Kaydet</DialogTitle><DialogDescription className="text-[#68687B]">Bugünkü antrenmanını kaydet.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Başlık</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn. Üst Vücut" data-testid="log-title" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Süre (dk)</label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving} data-testid="log-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
